"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { Waves, Sunset, Music, Heart, Briefcase, Users } from "lucide-react";

const EXPERIENCES = [
  {
    icon: Waves,
    title: "Pool Parties",
    description: "Dive into our infinity pool with 12 jacuzzi jets, waterfall feature, and poolside lounging.",
    image: "/images/pool-party-friends.jpg",
    accent: "from-blue-500/60",
  },
  {
    icon: Sunset,
    title: "Sky Deck Sunsets",
    description: "Sip cocktails on the panoramic sky deck as the sun sets behind Nandi Hills.",
    image: "/images/sunset-drinks-skydeck.jpg",
    accent: "from-orange-500/60",
  },
  {
    icon: Music,
    title: "Dance Nights",
    description: "Light up the private dance floor with professional audio, disco lights, and neon vibes.",
    image: "/images/dance-floor-entrance.jpg",
    accent: "from-purple-500/60",
  },
  {
    icon: Heart,
    title: "Romantic Getaways",
    description: "Rose petal setups, candlelit dinners, and intimate spaces for unforgettable celebrations.",
    image: "/images/romantic-rose-petals.jpg",
    accent: "from-rose-500/60",
  },
  {
    icon: Briefcase,
    title: "Corporate Retreats",
    description: "Open-air conference room, games zone, and team-building spaces for productive offsites.",
    image: "/images/conference-room-aerial.jpg",
    accent: "from-emerald-500/60",
  },
  {
    icon: Users,
    title: "Family Celebrations",
    description: "5 bedrooms, a full kitchen, and acres of space — perfect for birthdays and reunions.",
    image: "/images/dining-full-view.jpg",
    accent: "from-amber-500/60",
  },
];

export function ExperiencesSection() {
  return (
    <section className="section-padding bg-card/30">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
            Curated For You
          </p>
          <h2 className="mt-3 font-playfair text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            Experiences at Mountain Nest
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Every corner of the villa is designed for a different kind of magic
          </p>
        </motion.div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {EXPERIENCES.map((exp, i) => {
            const Icon = exp.icon;
            return (
              <motion.div
                key={exp.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div className="relative aspect-[3/4] sm:aspect-[4/5]">
                  <Image
                    src={exp.image}
                    alt={exp.title}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${exp.accent} via-black/40 to-transparent`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                </div>

                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                  <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-primary/20 backdrop-blur-sm border border-primary/30">
                    <Icon className="size-5 text-primary" />
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-white">
                    {exp.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/75 line-clamp-2">
                    {exp.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
